import { fetchAllMachines } from "../../repositories/machinesRepository";
import { MachineRow } from "../../types/operations";

// =============================================
// An in-memory map keyed by "Type|Number"
// (e.g. "CNC|1") that resolves to a machine UUID.
// =============================================
type MachineLookupMap = Map<string, string>;

// Normalise to uppercase so "CNC", "Cnc", and "cnc" all resolve the same entry.
function buildLookupKey(type: string, number: number): string {
  return `${type.toUpperCase()}|${number}`;
}

// =============================================
// Load all machines from the database and
// return a lookup map that resolves machine
// type + number to a Supabase machine UUID.
//
// Throws if the database call fails.
// =============================================
export async function buildMachineLookupMap(): Promise<MachineLookupMap> {
  const machines: MachineRow[] = await fetchAllMachines();
  const lookupMap: MachineLookupMap = new Map();

  for (const machine of machines) {
    const key = buildLookupKey(machine.type, machine.number);
    lookupMap.set(key, machine.id);
  }

  return lookupMap;
}

// =============================================
// Resolve a machine type and number to a UUID.
// Returns null if the machine is not found in
// the lookup map (i.e. not in the database).
// =============================================
export function resolveMachineId(
  lookupMap: MachineLookupMap,
  machineType: string,
  machineNumber: number
): string | null {
  const key = buildLookupKey(machineType, machineNumber);
  return lookupMap.get(key) ?? null;
}
